import { PaymentMethod } from "./Organization";
import { RoomBookingChangeProposal } from "./RoomBookingChange";
import { RequestedAdditionalGear } from "./AdditionalGear";
import { BookingPasscodeInfo } from "./SmartLock";
import type { CancellationPolicy } from "./CancellationPolicy";
import type { PassBookingLink, PassBookingState } from "./Pass";
import type { AnalyticsPayload } from "../utils/analytics";

/** Canonical definition moved to `models/Pass.ts`; re-exported here for existing imports. */
export type { PassBookingState };

export enum PaymentStatus {
  TO_BE_PAID_ON_SITE = 'TO_BE_PAID_ON_SITE',
  BLOCK_REQUESTED = 'BLOCK_REQUESTED',
  AMOUNT_BLOCKED = 'AMOUNT_BLOCKED',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
}

export enum StripeRefundStatus {
  PENDING = 'PENDING',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
  REQUIRES_ACTION = 'REQUIRES_ACTION'
}

export enum RoomBookingStatus {
  PENDING_CONFIRMATION = 'PENDING_CONFIRMATION',
  CONFIRMED_BY_HOST = 'CONFIRMED_BY_HOST',
  AUTO_CONFIRMED = 'AUTO_CONFIRMED',
  AUTO_CONFIRMED_PENDING_PAYMENT = 'AUTO_CONFIRMED_PENDING_PAYMENT',
  CANCELLED_BY_USER = 'CANCELLED_BY_USER',
  CANCELLED_BY_HOST = 'CANCELLED_BY_HOST',
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  CANCELLED_DUE_AMOUNT_BLOCK_EXPIRATION = 'CANCELLED_DUE_AMOUNT_BLOCK_EXPIRATION',
  CANCELLED_DUE_NO_RESPONSE_FROM_HOST = 'CANCELLED_DUE_NO_RESPONSE_FROM_HOST'
}

/** Calendar deep links returned by the API for a single booking occurrence. */
export interface BookingCalendarLinks {
  google: string;
  ics: string;
}

export interface RoomBooking {
  roomBookingId: string;
  /** Space id for this booking (same as details `spaceId`). */
  spaceId?: string | null;
  /** Booking owner profile image from the API; optional. */
  userProfileImageUrl?: string | null;
  /** First space image for list thumbnails. */
  spaceImageUrl?: string | null;
  /** First room image for list thumbnails. */
  roomImageUrl?: string | null;
  totalAmount: number;
  currencyCode: string;
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  /** When payment was collected/confirmed (cash on site or Stripe). Null until paid. */
  paymentCollectedAt?: string | null;
  stripeRefundStatus?: StripeRefundStatus;
  spaceName: string;
  roomName: string;
  startTime: string;
  endTime: string;
  /** Space IANA timezone for this booking, e.g. Europe/Warsaw. */
  timezone?: string;
  status: RoomBookingStatus;
  createdAt?: string;
  changeProposal?: RoomBookingChangeProposal;
  /** ID linking all occurrences in a recurring series. null for single bookings. */
  recurringSeriesId?: string | null;
  /** Smart lock passcode generation outcome. null when no linked lock or not generated yet. */
  passcodeInfo?: BookingPasscodeInfo | null;
  /** Add-to-calendar URLs for this occurrence. */
  calendarLinks?: BookingCalendarLinks | null;
  /** Org cancellation/refund rules for this booking. */
  cancellationPolicy?: CancellationPolicy | null;
  /** Present when this booking was paid for (fully or partially) with a pass. */
  pass?: PassBookingLink | null;
}

export interface ParticipantDetails {
  bandName?: string;
  participantCount: number;
  details?: string;
}

export type BookingOrigin = 'SELF_SERVICE' | 'MANUAL';

/** One session in a multi-session room booking. The first start and duration must match the request. */
export interface RoomBookingOccurrenceRequest {
    startTime: string;
    durationInMinutes: number;
    additionalGears?: RequestedAdditionalGear[];
}

export interface CreateRoomBookingRequest {
    roomId: string;
    startTime: string;
    durationInMinutes: number;
    additionalGears: RequestedAdditionalGear[];
    /** Optional so it can be omitted when paying with `passId` instead (XOR with paymentMethod). */
    paymentMethod?: PaymentMethod;
    participantDetails?: ParticipantDetails;
    regulationAccepted?: boolean;
    /** Number of weekly occurrences in a recurring series. Omit or null = single booking. */
    recurringWeeks?: number | null;
    recurringMonths?: number | null;
    /**
     * Explicit sessions, including the first. Omit for a single booking.
     * Do not send together with recurringWeeks or recurringMonths.
     */
    occurrences?: RoomBookingOccurrenceRequest[] | null;
    passId?: string | null;
    analytics?: AnalyticsPayload;
}

export type { AttributionTouch, AnalyticsPayload as BookingAttributionPayload } from '../utils/analytics';

/** POST /v1/host/room-bookings/manual */
export interface CreateManualRoomBookingRequest extends CreateRoomBookingRequest {
    userId?: string | null;
    phoneNumber?: string | null;
    sendSms?: boolean | null;
}

export enum StripeChargeType {
  DIRECT = "DIRECT",
  DESTINATION = "DESTINATION",
}

export interface CreateRoomBookingResponse {
  id: string;
  /** When the booking was created (ISO-8601 local date-time from the API). */
  createdAt?: string;
  status?: RoomBookingStatus;
  /** ID linking all occurrences in a series. null when single booking. */
  recurringSeriesId?: string | null;
  /** Number of bookings created (occurrences). */
  recurringCount?: number;
  stripePaymentIntentClientSecret?: string;
  stripePaymentIntentId?: string;
  stripeChargeType?: StripeChargeType;
  stripeAccountId?: string;
  bookingOrigin?: BookingOrigin;
  bookingLinkUrl?: string | null;
  passId?: string | null;
  passBookingState?: PassBookingState | null;
}

/** GET /v1/host/room-bookings/needs-host-action/count */
export interface RoomBookingNeedsHostActionCountResponse {
  needsHostActionCount: number;
}

/** GET /v1/user/room-bookings/needs-user-action/count */
export interface RoomBookingNeedsUserActionCountResponse {
  needsUserActionCount: number;
}